import React, { useContext, useEffect } from "react";
import shallow from "zustand/shallow";
import { useHistory } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers";
import { useForm, FormProvider } from "react-hook-form";
import { inventoryValidationSchema } from "../validation/inventoryValidationSchema";

import useRoute from "hooks/useRoute";
import useCurrentVehicleStore from "stores/InventoryStore";
import useCurrentPhotoStore from "stores/PhotoStore";
import InventoryDetailsTab from "../subFeatures/inventoryTabs/detailsTab/InventoryDetailsTab";
import InventoryPhotosTab from "../subFeatures/inventoryTabs/photosTab/InventoryPhotosTab";
import InventoryCostsTab from "../subFeatures/inventoryTabs/costsTab/InventoryCostsTab";
import InventoryKbbTab from "../subFeatures/inventoryTabs/valuesTab/InventoryKbbTab";
import NavBar from "components/tabs/TabsHolder";
import Loading from "components/loadingSpinner/Loading.js";
import { showApiError } from "utils/errorRoutingUtils";
import { retrieveFailAlert } from "utils/alertUtils";
import { useFromUser } from "stores/LocalStorageHelper";
import { getAllVendorSortedByLocationID } from "api/VendorsAPI";
import { failAlert } from "utils/alertUtils";
import { getAllInvDetails } from "api/InventoryAPI";
import {
	KbbContext,
	KbbValuesProvider,
} from "../subFeatures/inventoryTabs/valuesTab/KbbContext";
import { PATHS } from "constants/Constants";
import { getThumbnailURL } from "../subFeatures/inventoryTabs/photosTab/helperFunctions";
import InventoryFormsTab from "../subFeatures/inventoryTabs/formsTab/InventoryFormsTab";
// import InventoryFlooringTab from "../../features/inventory/subFeatures/inventoryTabs/InventoryFlooringTab";
import InventoryHistoryTab from "../subFeatures/inventoryTabs/historyTab/InventoryHistoryTab";
import InventoryActionBar from "./InventoryActionBar";
import dayjs from "dayjs";
import InventoryTitle from "../subFeatures/inventoryTabs/titleTab/InventoryTitle";
import { TemplateVariableProvider } from "context/TemplateVariableContext";
import InitializeTemplateVars from "utils/initializeTemplateVars";
import InventoryValuesTab from "../subFeatures/inventoryTabs/valuesTab/InventoryValuesTab";
import { MmrContext, MMRValuesProvider } from "../../../context/MMRContext";
import useIsOwnerOrAdmin from "../../settings/subFeatures/settingsTabs/userManagement/useIsOwnerOrAdmin";
import UnauthorizedContent from "../../deal/subFeatures/dealTabs/tabsContainer/UnauthorizedContent";
import useUserPrivileges from "../../settings/subFeatures/settingsTabs/userManagement/useUserPrivileges";
import InventoryAuctionsTab from "../subFeatures/inventoryTabs/AuctionsTab/InventoryAuctionsTab";
import FormErrorBoundary from "components/errorBoundary/FormErrorBoundary";

const isAppraisalPage = window.location.pathname === "/inventory-appraisal";

const InventoryDetails = () => {
	const history = useHistory();
	const newRoute = useRoute(PATHS.INVENTORY_LIST);
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");
	const firstName = useFromUser("firstName");
	const lastName = useFromUser("lastName");
	const dcLoginID = useFromUser("ID");
	const today = dayjs().format("YYYY-MM-DD");
	const isOwnerOrAdmin = useIsOwnerOrAdmin();
	const privileges = useUserPrivileges();
	const p = privileges();

	// kbb vehicle values
	const {
		stockNo,
		vin,
		year,
		make,
		model,
		odometerIn,
		editOdometerIn,
		editVin,
		editYear,
		editMake,
		editModel,
	} = useCurrentVehicleStore((state) => state, shallow);

	const { inventoryID, status } = useCurrentVehicleStore(
		(state) => state,
		shallow
	);

	// Vehicle store
	const {
		isLoading,
		setInventoryFields,
		editIsLoadingFalse,
		reset,
		editVendorsList,
		editReconVendorsList,
		editFlooringVendorsList,
		editCostHist,
		editCostDescList,
		editDepartmentsList,
		editAllVendorList,
		editLocationsList,
		editBuyersList,
		editTradeLenderList,
		editThumbnail,
		editModifiedOn,
		editModifiedByID,
		editOpenedBy,
		// different
		isVinLoading,
	} = useCurrentVehicleStore((state) => state, shallow);

	const { setDcKbbObj } = useContext(KbbContext);
	const { setVehicleMMRValues, setDefaultMMR } = useContext(MmrContext);

	const vehicleKbbObj = {
		stockNo,
		vin,
		year,
		make,
		model,
		odometerIn,
		editOdometerIn,
		editVin,
		editYear,
		editMake,
		editModel,
	};

	// Photo store
	const { resetImages } = useCurrentPhotoStore(
		(state) => ({
			resetImages: state.reset,
		}),
		shallow
	);

	// Gets all the inventory related details
	const getInvDetails = async () => {
		console.log(history);
		const vehicleID = inventoryID || history.location.state?.ID;
		console.log("Getting inventory details for: " + inventoryID);
		console.log("Getting inventory details for: " + vehicleID);

		if (vehicleID != null) {
			const allInfo = getAllInvDetails(dealerID, locationID, vehicleID);
			const allVendors = getAllVendorSortedByLocationID(locationID);

			Promise.all([allInfo, allVendors]).then(
				(res) => {
					updateStores(res);
					console.log(res);
				},
				(err) => {
					console.log(err);
					if (!err.isGeneralError) {
						showApiError(err, retrieveFailAlert);
						history.push(newRoute);
					}
				}
			);
		} else if (vehicleID == null) {
			history.push(newRoute);
			failAlert("Missing ID");
		} else {
			history.push(newRoute);
			retrieveFailAlert();
		}
	};

	// Updates relevant store/context values with API call
	const updateStores = (res) => {
		const invInfo = res[0].data.content;
		const allVendors = res[1].data.content;
		console.log(invInfo);
		console.log(invInfo.vehicleLocations);

		// Edit vehicle details tab
		setInventoryFields(invInfo.vehicle);
		setDcKbbObj(invInfo.kbb);
		if (invInfo.mmr === null) {
			setDefaultMMR();
		} else {
			setVehicleMMRValues(invInfo.mmr);
		}

		// Edit costs tab
		editCostHist(invInfo.reconditioning.reconditioningList);
		editCostDescList(invInfo.reconditioning.description);
		editDepartmentsList(invInfo.reconditioning.department);
		editLocationsList(invInfo.vehicleLocations);
		editBuyersList(invInfo.vehicleBuyers);
		// Edit vendors arrays
		editAllVendorList(allVendors);
		editVendorsList(allVendors.vendors);
		editReconVendorsList(allVendors.reconSupplier);
		editFlooringVendorsList(allVendors.flooringVendor);
		editTradeLenderList(allVendors.lenders);

		editThumbnail(getThumbnailURL(invInfo.images));

		//edit more information
		editModifiedOn(today);
		editModifiedByID(dcLoginID);
		editOpenedBy(lastName + ", " + firstName);
		editIsLoadingFalse();
	};

	useEffect(() => {
		getInvDetails();
		return () => {
			reset();
			resetImages();
		};
		// eslint-disable-next-line
	}, []);

	const tabs = [
		"Details",
		"Photos",
		"Costs",
		"Values",
		// "Flooring",
		"Forms",
		"History",
		"Auctions",
	];

	const tabComponents = [
		<InventoryDetailsTab />,
		<InventoryPhotosTab />,
		p.inventoryCostsTab ? <InventoryCostsTab /> : <UnauthorizedContent />,
		<InventoryValuesTab
			inventoryID={inventoryID}
			vehicleKbbObj={vehicleKbbObj}
			showVinDecoder={isAppraisalPage}
		/>,
		// <InventoryKbbTab
		// 	inventoryID={inventoryID}
		// 	vehicleKbbObj={vehicleKbbObj}
		// 	showVinDecoder={isAppraisalPage}
		// />,
		// <InventoryFlooringTab />,
		<InventoryFormsTab />,
		<InventoryHistoryTab />,
		<InventoryAuctionsTab />,
	];

	return (
		<div style={{ minHeight: "1000px" }}>
			{isLoading && <Loading containerStyle={{ height: "80vh" }} />}
			{!isLoading && (
				<div className="col-md-12 px-0">
					<InventoryActionBar />
					{!isVinLoading && (
						<>
							<InventoryTitle />
							<NavBar
								navClass="nav-pills-primary justify-content-center nav nav-pills"
								tabs={tabs}
								tabComponents={tabComponents}
							/>
						</>
					)}
				</div>
			)}
		</div>
	);
};

const Providers = ({ children }) => {
	// Define form validation parameters using centralized schema
	const methods = useForm({
		reValidateMode: "onBlur",
		resolver: yupResolver(inventoryValidationSchema),
	});

	return (
		<FormErrorBoundary>
			<KbbValuesProvider>
				<TemplateVariableProvider>
					<MMRValuesProvider>
						<InitializeTemplateVars />
						<FormProvider {...methods}>{children}</FormProvider>
					</MMRValuesProvider>
				</TemplateVariableProvider>
			</KbbValuesProvider>
		</FormErrorBoundary>
	);
};

const InventoryDetailPage = () => {
	return (
		<Providers>
			<InventoryDetails />
		</Providers>
	);
};

export default InventoryDetailPage;
